import React from 'react';
import PropTypes from 'prop-types';

import { IconButton } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

import { Draggable } from 'react-beautiful-dnd';

import Description from 'ui/text/Description';
import CustomChip from 'ui/CustomChip';

// Store sales can eat into an event's unsold quota before the reservation
// window opens, so the promoter may no longer be able to bring the quantity
// they configured. When that happens the store badge turns into a warning
// carrying the real number to bring: units already sold at the event (they
// are collected there) plus what can still be sold.
const quotaShortfall = (product) => product.storefront_visible && product.allocation_quantity &&
  product.available_amount != null && product.available_amount < (product.allocation_quantity - product.sold_count);

const chips = [
  { key: 'exclusive', shouldShow: (product) => !product.storefront_visible, label: 'exclusive' },
  { key: 'store', shouldShow: (product) => product.storefront_visible && !quotaShortfall(product), label: (product) => product.sold_count > 0
      ? `store (${product.sold_count}/${product.allocation_quantity} units sold)`
      : `store (${product.allocation_quantity} units)` },
  { key: 'variants',
    shouldShow: (product) => product.variant_allocations?.length > 0,
    label: (product) => product.variant_allocations
      .map((row) => `${row.label} ×${row.allocation_quantity}`)
      .join(' · ') },
  { key: 'shortfall',
    shouldShow: quotaShortfall,
    label: (product) => `store, ${product.sold_count} units sold, ${product.available_amount} more available (take ${product.sold_count + product.available_amount} only)` },
  { key: 'inactive', shouldShow: (product) => !product.available, label: 'inactive' },
  { key: 'free', shouldShow: (product) => !product.sellable, label: 'free' },
  { key: 'secret', shouldShow: (product) => product.secret, label: 'secret' },
  { key: 'discount', shouldShow: (product) => product.has_discounts, label: 'discount' },
];

const ListItem = ({ product, disabled, handleDeleteRequest, handleEditQuantity, handleRedirect, index }) => {
  return (
    <Draggable draggableId={String(product.listing_id)} index={index} isDragDisabled={disabled}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          className={`event-form_ticket draggable-perk ${snapshot.isDragging ? "dragging" : ""}`}
        >
          <div className="event-form_ticket_info">
            <Description>{product.identifier}</Description>
          </div>
          <div className="ticket-chip-container">
            {chips.filter(({ shouldShow }) => shouldShow(product)).map(({ key, label }) => (
              <CustomChip key={key} styleKey={key} label={typeof label === 'function' ? label(product) : label} minwidth padded />
            ))}
          </div>
          <div className="active-offers-buttons">
            <IconButton
              disabled={disabled}
              onClick={(e) => product.storefront_visible
                ? handleEditQuantity(product)
                : handleRedirect(e, `/${product.product_id}/edit`)}>
              <EditIcon color={disabled ? "disabled" : "warning"} />
            </IconButton>
            <IconButton disabled={disabled} onClick={() => handleDeleteRequest(product)}>
              <DeleteIcon color={disabled ? "disabled" : "warning"} />
            </IconButton>
            <DragIndicatorIcon color={disabled ? "disabled" : "warning"} />
          </div>
        </div>
      )}
    </Draggable>
  );
};

ListItem.propTypes = {
  product: PropTypes.object,
  disabled: PropTypes.bool,
  handleDeleteRequest: PropTypes.func,
  handleEditQuantity: PropTypes.func,
  handleRedirect: PropTypes.func,
  index: PropTypes.number,
};

export default ListItem;
